import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BookOpen, Plus, Pencil, Trash2, Copy, Eye, Search, X,
    Loader2, Sparkles, Clock, Hash, RefreshCw, ChevronDown, Upload,
    HelpCircle, ArrowUpDown, CheckSquare, Square, Calendar, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as apiClient from '@/services/apiClient';
import PageHeader from '@/components/ux/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { NewQuestionSetModal } from '@/components/questionsets/NewQuestionSetModal';
import { QuestionSetEditorModal } from '@/components/questionsets/QuestionSetEditorModal';
import { UploadQuestionSetModal } from '@/components/questionsets/UploadQuestionSetModal';
import { QuestionPreviewModal } from '@/components/questionsets/QuestionPreviewModal';
import { QuestionBankHelpDrawer } from '@/components/questionsets/QuestionBankHelpDrawer';
import { ViewToggle, useViewMode } from '@/components/ux/ViewToggle';

// ── Helpers ───────────────────────────────────────────────────────────────────
const DIFF_COLORS = {
    easy:   'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    medium: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    hard:   'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/25',
};
const TYPE_COLORS = {
    technical:   'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/25',
    behavioral:  'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/25',
    coding:      'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/25',
};
function typeColor(name = '') {
    const k = name.toLowerCase();
    if (k.includes('coding')) return TYPE_COLORS.coding;
    if (k.includes('behav'))  return TYPE_COLORS.behavioral;
    return TYPE_COLORS.technical;
}
function diffColor(name = '') { return DIFF_COLORS[(name || '').toLowerCase()] || DIFF_COLORS.medium; }

function timeAgo(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const secs = Math.floor((Date.now() - d) / 1000);
    if (secs < 60)    return 'just now';
    if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────
function DeleteConfirmDialog({ open, setName, onConfirm, onClose, loading }) {
    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="text-destructive">Delete question set?</DialogTitle>
                    <DialogDescription>
                        <strong>"{setName}"</strong> will be removed. Sessions already
                        created from this set are not affected.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button variant="destructive" onClick={onConfirm} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ── Rename Dialog (also edits description) — Phase 8f ─────────────────────────
function RenameDialog({ open, set, onSave, onClose }) {
    const [name,        setName]        = useState('');
    const [description, setDescription] = useState('');
    const [saving,      setSaving]      = useState(false);

    useEffect(() => {
        if (open && set) {
            setName(set.name || '');
            setDescription(set.description || '');
        }
    }, [open, set]);

    const handleSave = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try { await onSave(name.trim(), description.trim()); onClose(); }
        catch (err) { toast.error(err.message || 'Failed.'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Edit question set</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="space-y-1.5">
                        <Label>Name</Label>
                        <Input value={name} onChange={e => setName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSave()} />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={2}
                            placeholder="Brief note about this set…"
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving || !name.trim()}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ── QuestionSetCard ───────────────────────────────────────────────────────────
function QuestionSetCard({ set, onEdit, onRename, onDuplicate, onDelete, onUse, onPreview, bulkMode, selected, onToggleSelect }) {
    return (
        <div className={cn(
            'group rounded-xl border bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all',
            selected ? 'border-primary bg-primary/5' : 'border-border',
        )}>
            <div className="flex items-start gap-3">
                {/* Bulk checkbox — Phase 8h */}
                {bulkMode && (
                    <button type="button" onClick={onToggleSelect} className="mt-0.5 shrink-0 text-primary">
                        {selected
                            ? <CheckSquare className="h-4 w-4" />
                            : <Square className="h-4 w-4 text-muted-foreground" />}
                    </button>
                )}

                <div className="flex-1 min-w-0 space-y-2">
                    {/* Name — clickable for preview (Phase 8c) */}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={onPreview}
                            className="font-semibold text-sm text-foreground hover:text-primary hover:underline truncate text-left"
                        >
                            {set.name}
                        </button>
                        {/* Round badge — Phase 8e */}
                        {set.roundNumber && (
                            <span className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                <Layers className="h-2.5 w-2.5" /> Round {set.roundNumber}
                            </span>
                        )}
                        <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', typeColor(set.interviewTypeName))}>
                            {set.interviewTypeName || '—'}
                        </span>
                        <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', diffColor(set.difficultyName))}>
                            {set.difficultyName || '—'}
                        </span>
                    </div>

                    {/* Stats row */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {set.numberOfQuestions || '?'} questions
                        </span>
                        <span className="flex items-center gap-1"
                            title={set.actualDurationMinutes ? 'Total of the question time limits' : undefined}>
                            <Clock className="h-3 w-3" />
                            {set.actualDurationMinutes ?? set.durationMinutes ?? '?'} min
                        </span>
                        {set.usageCount > 0 && (
                            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                <RefreshCw className="h-3 w-3" />
                                Used {set.usageCount}×
                            </span>
                        )}
                        {/* Last used — Phase 8d */}
                        {set.lastUsedAt ? (
                            <span className="flex items-center gap-1" title={new Date(set.lastUsedAt).toLocaleString()}>
                                <Calendar className="h-3 w-3" />
                                Last used {timeAgo(set.lastUsedAt)}
                            </span>
                        ) : (
                            <span className="text-muted-foreground/50">Never used</span>
                        )}
                    </div>

                    {/* Description */}
                    {set.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{set.description}</p>
                    )}
                </div>

                {/* Action buttons */}
                {!bulkMode && (
                    <div className="flex items-center gap-1 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} title="Edit questions">
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRename} title="Edit name & description">
                            <BookOpen className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDuplicate} title="Duplicate">
                            <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive" onClick={onDelete} title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Use → shortcut */}
            {!bulkMode && (
                <div className="mt-3 pt-3 border-t border-border/60 flex justify-end">
                    <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={onUse}>
                        <Eye className="h-3 w-3" /> Select to Schedule
                    </Button>
                </div>
            )}
        </div>
    );
}

// ── QuestionSetRow — dense alternative to the card ────────────────────────────
function QuestionSetRow({ set, onEdit, onRename, onDuplicate, onDelete, onUse, onPreview, bulkMode, selected, onToggleSelect }) {
    return (
        <div className={cn(
            'group grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2.5 transition-colors last:border-0 hover:bg-muted/40',
            selected && 'bg-primary/5',
        )}>
            <div className="col-span-12 min-w-0 sm:col-span-4 flex items-center gap-2">
                {/* Bulk checkbox — Phase 8h */}
                {bulkMode && (
                    <button type="button" onClick={onToggleSelect} className="shrink-0 text-primary">
                        {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4 text-muted-foreground" />}
                    </button>
                )}
                <div className="min-w-0">
                    {/* Clickable name for preview — Phase 8c */}
                    <button
                        type="button"
                        onClick={onPreview}
                        className="truncate text-xs font-semibold text-foreground hover:text-primary hover:underline text-left block w-full"
                    >
                        {set.name}
                    </button>
                    {set.description && (
                        <p className="truncate text-[11px] text-muted-foreground">{set.description}</p>
                    )}
                </div>
            </div>

            <div className="col-span-6 flex flex-wrap items-center gap-1 sm:col-span-3">
                {/* Round badge — Phase 8e */}
                {set.roundNumber && (
                    <span className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                        R{set.roundNumber}
                    </span>
                )}
                <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', typeColor(set.interviewTypeName))}>
                    {set.interviewTypeName || '—'}
                </span>
                <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', diffColor(set.difficultyName))}>
                    {set.difficultyName || '—'}
                </span>
            </div>

            <div className="col-span-6 flex items-center gap-3 text-[11px] text-muted-foreground sm:col-span-2">
                <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{set.numberOfQuestions ?? '?'}</span>
                <span className="flex items-center gap-1"
                    title={set.actualDurationMinutes
                        ? 'Total of the question time limits — what the candidate actually gets'
                        : undefined}>
                    <Clock className="h-3 w-3" />{set.actualDurationMinutes ?? set.durationMinutes ?? '?'}m
                </span>
            </div>

            {/* Usage + last used — Phase 8d */}
            <div className="col-span-6 hidden text-[11px] text-muted-foreground sm:col-span-1 sm:block">
                <div>{set.usageCount > 0 ? `${set.usageCount}×` : '—'}</div>
                {set.lastUsedAt && (
                    <div className="text-[10px] flex items-center gap-0.5" title={new Date(set.lastUsedAt).toLocaleString()}>
                        <Calendar className="h-2.5 w-2.5" />{timeAgo(set.lastUsedAt)}
                    </div>
                )}
            </div>

            <div className="col-span-12 flex items-center justify-end gap-0.5 sm:col-span-2">
                {!bulkMode && (
                    <>
                        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[11px]" onClick={onUse} title="Select to schedule">
                            <Eye className="h-3 w-3" />Use
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} aria-label="Edit questions">
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRename} aria-label="Edit name & description">
                            <BookOpen className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDuplicate} aria-label="Duplicate set">
                            <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive" onClick={onDelete} aria-label="Delete set">
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </>
                )}
                {bulkMode && (
                    <button type="button" onClick={onToggleSelect} className="text-primary px-2">
                        {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4 text-muted-foreground" />}
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function QuestionSetsPage() {
    const navigate = useNavigate();

    const [sets,          setSets]          = useState([]);
    const [templates,     setTemplates]     = useState([]);
    const [masterTypes,   setMasterTypes]   = useState([]);
    const [masterDiffs,   setMasterDiffs]   = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState('');

    // Filters
    const [search,        setSearch]        = useState('');
    const [filterTpl,     setFilterTpl]     = useState('');
    const [viewMode,      setViewMode]      = useViewMode('echohire.questionSets.view');

    // Filters — Phase 8a
    const [filterType,    setFilterType]    = useState('');
    const [filterDiff,    setFilterDiff]    = useState('');
    // Sort — Phase 8b
    const [sortBy,        setSortBy]        = useState('');
    // Bulk delete — Phase 8h
    const [bulkMode,      setBulkMode]      = useState(false);
    const [selectedIds,   setSelectedIds]   = useState(new Set());
    const [bulkDeleting,  setBulkDeleting]  = useState(false);
    // Preview modal — Phase 8c
    const [previewSet,    setPreviewSet]    = useState(null);
    const [showHelp,      setShowHelp]      = useState(false);

    // Modal state
    const [showNew,       setShowNew]       = useState(false);
    const [showUpload,    setShowUpload]    = useState(false);

    // Arriving from the scheduling page with a role in hand: filter to it and
    // open the create dialog, rather than landing on an unfiltered list.
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const tid = searchParams.get('templateId');
        if (tid) setFilterTpl(tid);
        if (searchParams.get('create') === '1') setShowNew(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [editorSet,     setEditorSet]     = useState(null);    // full set with questions
    const [editorLoading, setEditorLoading] = useState(false);
    const [deleteTarget,  setDeleteTarget]  = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [renameTarget,  setRenameTarget]  = useState(null);

    // ── Fetch ────────────────────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [setsRes, tplRes, mastersRes] = await Promise.all([
                apiClient.getQuestionSets(),
                apiClient.getTemplates(),
                apiClient.getMasters(),
            ]);
            setSets(setsRes.data || []);
            setTemplates(tplRes.data || []);
            const masters = mastersRes.data || {};
            setMasterTypes((masters.interviewType || []).filter(m => m.isActive));
            setMasterDiffs((masters.difficulty    || []).filter(m => m.isActive));
            setError('');
        } catch (err) {
            setError('Failed to load question sets.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Open editor (fetch full set) ─────────────────────────────────────────
    const openEditor = async (setId) => {
        setEditorLoading(true);
        try {
            const res = await apiClient.getQuestionSet(setId);
            setEditorSet(res.data);
        } catch (err) {
            toast.error('Failed to load question set.');
        } finally {
            setEditorLoading(false);
        }
    };

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await apiClient.deleteQuestionSet(deleteTarget._id);
            setSets(prev => prev.filter(s => s._id !== deleteTarget._id));
            toast.success(`"${deleteTarget.name}" deleted.`);
            setDeleteTarget(null);
        } catch (err) {
            toast.error(err.message || 'Failed to delete.');
        } finally {
            setDeleteLoading(false);
        }
    };

    // ── Duplicate ────────────────────────────────────────────────────────────
    const handleDuplicate = async (set) => {
        try {
            const res = await apiClient.duplicateQuestionSet(set._id);
            setSets(prev => [res.data, ...prev]);
            toast.success(`Duplicated as "${res.data.name}".`);
        } catch (err) {
            toast.error(err.message || 'Failed to duplicate.');
        }
    };

    // ── Rename ───────────────────────────────────────────────────────────────
    const handleRename = async (newName, newDescription) => {
        await apiClient.updateQuestionSetMeta(renameTarget._id, { name: newName, description: newDescription });
        setSets(prev => prev.map(s => s._id === renameTarget._id
            ? { ...s, name: newName, description: newDescription }
            : s));
        if (editorSet?._id === renameTarget._id)
            setEditorSet(prev => ({ ...prev, name: newName, description: newDescription }));
        toast.success('Saved.');
    };

    // ── After new set created → open editor ──────────────────────────────────
    const handleCreated = (newSet) => {
        setSets(prev => [newSet, ...prev]);
        setEditorSet(newSet);
        toast.success(`"${newSet.name}" created with ${newSet.questions?.length} questions. Edit below.`);
    };

    // ── After editor saves ────────────────────────────────────────────────────
    const handleEditorSaved = (updatedSet) => {
        setSets(prev => prev.map(s => s._id === updatedSet._id
            ? { ...s, numberOfQuestions: updatedSet.questions?.length }
            : s
        ));
    };

    // ── Preview — Phase 8c ────────────────────────────────────────────────────
    const openPreview = async (set) => {
        if (set.questions) { setPreviewSet(set); return; }
        try {
            const res = await apiClient.getQuestionSet(set._id);
            setPreviewSet(res.data);
        } catch { toast.error('Failed to load preview.'); }
    };

    // ── Bulk delete — Phase 8h ─────────────────────────────────────────────────
    const [bulkConfirm,  setBulkConfirm]  = useState(false);

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setBulkConfirm(false);
        setBulkDeleting(true);
        try {
            await Promise.all([...selectedIds].map(id => apiClient.deleteQuestionSet(id)));
            setSets(prev => prev.filter(s => !selectedIds.has(s._id)));
            toast.success(`${selectedIds.size} set${selectedIds.size !== 1 ? 's' : ''} deleted.`);
            setSelectedIds(new Set());
            setBulkMode(false);
        } catch (err) {
            toast.error(err.message || 'Bulk delete failed.');
        } finally {
            setBulkDeleting(false);
        }
    };

    const toggleSelect = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // ── Group by template ─────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        let s = sets;
        if (filterTpl)  s = s.filter(x => String(x.templateId?._id || x.templateId) === filterTpl);
        // Phase 8a — type + difficulty filters
        if (filterType) s = s.filter(x => x.interviewTypeMasterId === filterType || String(x.interviewTypeMasterId?._id || x.interviewTypeMasterId) === filterType);
        if (filterDiff) s = s.filter(x => x.difficultyMasterId   === filterDiff  || String(x.difficultyMasterId?._id  || x.difficultyMasterId)  === filterDiff);
        if (search.trim()) {
            const q = search.toLowerCase();
            s = s.filter(x =>
                x.name.toLowerCase().includes(q) ||
                x.interviewTypeName?.toLowerCase().includes(q) ||
                x.difficultyName?.toLowerCase().includes(q)
            );
        }
        // Phase 8b — sort
        if (sortBy === 'name_asc')    s = [...s].sort((a, b) => a.name.localeCompare(b.name));
        if (sortBy === 'name_desc')   s = [...s].sort((a, b) => b.name.localeCompare(a.name));
        if (sortBy === 'usage_desc')  s = [...s].sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0));
        if (sortBy === 'recent_asc')  s = [...s].sort((a, b) => new Date(a.lastUsedAt || 0) - new Date(b.lastUsedAt || 0));
        if (sortBy === 'recent_desc') s = [...s].sort((a, b) => new Date(b.lastUsedAt || 0) - new Date(a.lastUsedAt || 0));
        return s;
    }, [sets, filterTpl, filterType, filterDiff, search, sortBy]);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const s of filtered) {
            const tid   = String(s.templateId?._id || s.templateId || 'unknown');
            const tname = s.templateId?.title || 'Unknown Job Description';
            if (!map.has(tid)) map.set(tid, { tid, tname, items: [] });
            map.get(tid).items.push(s);
        }
        return [...map.values()];
    }, [filtered]);

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="space-y-4 p-1">
                <Skeleton className="h-8 w-48" />
                <div className="space-y-3">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* ── Page header ─────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-lg font-semibold tracking-tight">Question Sets</h1>
                    <p className="text-[11px] text-muted-foreground">
                        Reusable AI-generated question banks, grouped by job description
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Phase 9 — Help button */}
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="How to use Question Bank" onClick={() => setShowHelp(true)}>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    {/* Phase 8h — bulk select toggle */}
                    <Button
                        variant={bulkMode ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 gap-1.5 text-xs"
                        onClick={() => { setBulkMode(p => !p); setSelectedIds(new Set()); }}
                    >
                        {bulkMode ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                        {bulkMode ? 'Cancel' : 'Select'}
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setShowUpload(true)}>
                        <Upload className="h-3.5 w-3.5" />
                        Upload from file
                    </Button>
                    <Button size="sm" className="h-8 gap-1.5 shadow-sm" onClick={() => setShowNew(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        New Question Set
                    </Button>
                </div>
            </div>

            {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                    {error}
                </div>
            )}

            {/* ── Filters ─────────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[12rem] flex-1 max-w-xs">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search sets…" value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="h-8 pl-8 text-xs" />
                    {search && (
                        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                            onClick={() => setSearch('')}>
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <select
                    value={filterTpl}
                    onChange={e => setFilterTpl(e.target.value)}
                    className="h-8 rounded-md border border-input bg-background px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    <option value="">All job descriptions</option>
                    {templates.map(t => (
                        <option key={t._id} value={t._id}>{t.title}</option>
                    ))}
                </select>
                {/* Phase 8a — type filter */}
                <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className="h-8 rounded-md border border-input bg-background px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    <option value="">All types</option>
                    {masterTypes.map(t => (
                        <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                </select>
                {/* Phase 8a — difficulty filter */}
                <select
                    value={filterDiff}
                    onChange={e => setFilterDiff(e.target.value)}
                    className="h-8 rounded-md border border-input bg-background px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    <option value="">All difficulties</option>
                    {masterDiffs.map(d => (
                        <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                </select>
                {/* Phase 8b — sort */}
                <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="h-8 rounded-md border border-input bg-background px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    <option value="">Sort: Default</option>
                    <option value="name_asc">Name A→Z</option>
                    <option value="name_desc">Name Z→A</option>
                    <option value="usage_desc">Most used</option>
                    <option value="recent_desc">Recently used</option>
                    <option value="recent_asc">Least recently used</option>
                </select>
                {(search || filterTpl || filterType || filterDiff || sortBy) && (
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                        onClick={() => { setSearch(''); setFilterTpl(''); setFilterType(''); setFilterDiff(''); setSortBy(''); }}>
                        Clear
                    </Button>
                )}
                <ViewToggle value={viewMode} onChange={setViewMode} className="ml-auto" />
            </div>

            {/* ── Empty state ──────────────────────────────────────────────── */}
            {grouped.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
                        <BookOpen className="h-7 w-7 text-muted-foreground" />
                    </div>
                    <div>
                        <p className="font-semibold text-foreground">No question sets yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                            Generate a reusable set from any job description.
                        </p>
                    </div>
                    <Button size="sm" className="gap-1.5" onClick={() => setShowNew(true)}>
                        <Sparkles className="h-4 w-4" /> Generate first set
                    </Button>
                </div>
            )}

            {/* ── Grouped list ─────────────────────────────────────────────── */}
            {grouped.map(group => (
                <div key={group.tid} className="space-y-3">
                    {/* Template group header */}
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                            {group.tname}
                        </h2>
                        <Badge variant="secondary" className="text-[10px]">
                            {group.items.length} set{group.items.length !== 1 ? 's' : ''}
                        </Badge>
                    </div>

                    {viewMode === 'list' ? (
                        <div className="overflow-hidden rounded-xl border border-border bg-card">
                            <div className="hidden grid-cols-12 gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
                                <span className="col-span-4">Set</span>
                                <span className="col-span-3">Type &amp; difficulty</span>
                                <span className="col-span-2">Questions</span>
                                <span className="col-span-1">Used</span>
                                <span className="col-span-2" />
                            </div>
                            {group.items.map(set => (
                                <QuestionSetRow
                                    key={set._id}
                                    set={set}
                                    onEdit={() => openEditor(set._id)}
                                    onRename={() => setRenameTarget(set)}
                                    onDuplicate={() => handleDuplicate(set)}
                                    onDelete={() => setDeleteTarget(set)}
                                    onUse={() => navigate(`/admin/schedule?questionSetId=${set._id}`)}
                                    onPreview={() => openPreview(set)}
                                    bulkMode={bulkMode}
                                    selected={selectedIds.has(set._id)}
                                    onToggleSelect={() => toggleSelect(set._id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {group.items.map(set => (
                                <QuestionSetCard
                                    key={set._id}
                                    set={set}
                                    onEdit={() => openEditor(set._id)}
                                    onRename={() => setRenameTarget(set)}
                                    onDuplicate={() => handleDuplicate(set)}
                                    onDelete={() => setDeleteTarget(set)}
                                    onUse={() => navigate(`/admin/schedule?questionSetId=${set._id}`)}
                                    onPreview={() => openPreview(set)}
                                    bulkMode={bulkMode}
                                    selected={selectedIds.has(set._id)}
                                    onToggleSelect={() => toggleSelect(set._id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {/* Editor loading overlay */}
            {editorLoading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            )}

            {/* ── Modals ───────────────────────────────────────────────────── */}
            <NewQuestionSetModal
                open={showNew}
                onClose={() => setShowNew(false)}
                templates={templates}
                masterTypes={masterTypes}
                masterDiffs={masterDiffs}
                onCreated={handleCreated}
            />

            <UploadQuestionSetModal
                open={showUpload}
                onClose={() => setShowUpload(false)}
                templates={templates}
                masterTypes={masterTypes}
                masterDiffs={masterDiffs}
                onCreated={handleCreated}
            />

            <QuestionSetEditorModal
                open={!!editorSet}
                onClose={() => setEditorSet(null)}
                set={editorSet}
                onSaved={handleEditorSaved}
            />

            <DeleteConfirmDialog
                open={!!deleteTarget}
                setName={deleteTarget?.name || ''}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
                loading={deleteLoading}
            />

            <RenameDialog
                open={!!renameTarget}
                set={renameTarget}
                onSave={handleRename}
                onClose={() => setRenameTarget(null)}
            />

            {/* Phase 8c — Preview modal */}
            <QuestionPreviewModal
                open={!!previewSet}
                onClose={() => setPreviewSet(null)}
                set={previewSet}
                onEdit={() => previewSet && openEditor(previewSet._id)}
            />

            {/* Phase 9 — Help drawer */}
            <QuestionBankHelpDrawer
                open={showHelp}
                onClose={() => setShowHelp(false)}
            />

            {/* Phase 8h — Bulk action bar */}
            {bulkMode && selectedIds.size > 0 && (
                <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 flex items-center gap-3 rounded-xl border border-border bg-popover px-4 py-2.5 shadow-lg">
                    <span className="text-xs font-medium text-foreground">{selectedIds.size} selected</span>
                    {!bulkConfirm ? (
                        <Button
                            variant="destructive"
                            size="sm"
                            className="h-7 gap-1.5 text-xs"
                            onClick={() => setBulkConfirm(true)}
                            disabled={bulkDeleting}
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete selected
                        </Button>
                    ) : (
                        <>
                            <span className="text-xs text-destructive font-medium">Delete {selectedIds.size} set{selectedIds.size !== 1 ? 's' : ''}?</span>
                            <Button variant="destructive" size="sm" className="h-7 gap-1.5 text-xs"
                                onClick={handleBulkDelete} disabled={bulkDeleting}>
                                {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                Confirm
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs"
                                onClick={() => setBulkConfirm(false)}>
                                Cancel
                            </Button>
                        </>
                    )}
                    {!bulkConfirm && (
                        <Button variant="outline" size="sm" className="h-7 text-xs"
                            onClick={() => { setSelectedIds(new Set()); setBulkConfirm(false); }}>
                            Clear
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}
